import { Locale } from '@headless-workspace/config';
import { z } from 'zod';
import { LanguageHelpers } from './LanguageHelpers';
import { PredicateHelpers } from './PredicateHelpers';
import { IsomorphicLogger } from './logger';

const Logger = new IsomorphicLogger();

const safeParseArray = <T extends z.ZodTypeAny>(value: unknown[] | undefined | null, type: T): z.infer<T>[] => {
    if (!value) {
        return [];
    }
    return value
        .map((item) => {
            const result = type.safeParse(item);
            if (result.success) {
                return result.data;
            }

            Logger.warn('Parsing error', item, result.error);

            return undefined;
        })
        .filter(PredicateHelpers.notNullOrUndefined);
};

const defaultArray = <T extends z.ZodTypeAny>(
    type: T,
): z.ZodEffects<z.ZodDefault<z.ZodOptional<z.ZodNullable<z.ZodArray<T>>>>> => {
    return z
        .array(type)
        .nullish()
        .default([])
        .transform((val) => (val === null ? [] : val));
};

const parseByLocaleCountry = <T>(data: T | Record<string, T>, locale: Locale): T => {
    return data && typeof data === 'object' ? (data as Record<string, T>)[LanguageHelpers.parseCountry(locale)] : data;
};

const parseByLocaleLanguage = <T>(data: T | Record<string, T>, locale: Locale): T => {
    return data && typeof data === 'object' ? (data as Record<string, T>)[LanguageHelpers.parseLanguage(locale)] : data;
};

const caseInsensitiveLiteral = <T extends string>(literal: T) =>
    z
        .string()
        .transform((string) => string.toLowerCase())
        .pipe(z.literal(literal.toLowerCase() as T));

export const DecoderHelpers = {
    safeParseArray,
    defaultArray,
    parseByLocaleCountry,
    parseByLocaleLanguage,
    caseInsensitiveLiteral,
};
